import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { T } from "services/lang/LanguageService";
import { Dropdown } from "primereact/dropdown";
import { TimeOptions } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { useState, useEffect, ChangeEvent } from "react";
import { WeatherMonitoringProps } from "../charts/interfaces";

interface LineOptions {
  color: string;
  width: number;
  dash: string;
}

const lineOptions: { [key: string]: LineOptions } = {
  temperature: { color: "#FF0000", width: 2, dash: "solid" },
  feelsLikeTemperature: { color: "#FF6666", width: 2, dash: "dot" },
  humidity: { color: "#52B1D2", width: 2, dash: "solid" },
  windSpeed: { color: "#0000FF", width: 2, dash: "dot" },
  windDirection: { color: "#0000FF", width: 2, dash: "solid" },
  precipitation: { color: "#00b899", width: 2, dash: "solid" },
  gusture: { color: "#FFA500", width: 2, dash: "dot" },
};

const overriddenTitles: { [key: string]: string } = {
  temperature: T("word.temperature"),
  feelsLikeTemperature: T("word.feels-like-temperature"),
  humidity: T("word.humidity"),
  windSpeed: T("word.wind-speed"),
  windDirection: T("word.wind-direction"),
  precipitation: T("word.precipitation"),
  gusture: T("word.gusture"),
};

type WeatherMonitoringKey = keyof WeatherMonitoringProps;

const defaultConfig: WeatherMonitoringProps = {
  timeInterval: TimeOptions.DAYS.name,
  location: "",
  temperature: {
    isVisible: true,
    dataset: {
      identifier: "",
      line: lineOptions.temperature,
      overriddenTitle: overriddenTitles.temperature,
    },
  },
  feelsLikeTemperature: {
    isVisible: true,
    dataset: {
      identifier: "",
      line: lineOptions.feelsLikeTemperature,
      overriddenTitle: overriddenTitles.feelsLikeTemperature,
    },
  },
  humidity: {
    isVisible: true,
    dataset: {
      identifier: "",
      line: lineOptions.humidity,
      overriddenTitle: overriddenTitles.humidity,
    },
  },
  windSpeed: {
    isVisible: true,
    dataset: {
      identifier: "",
      line: lineOptions.windSpeed,
      overriddenTitle: overriddenTitles.windSpeed,
    },
  },
  windDirection: {
    isVisible: true,
    dataset: {
      identifier: "",
      line: lineOptions.windDirection,
      overriddenTitle: overriddenTitles.windDirection,
    },
  },
  precipitation: {
    isVisible: true,
    dataset: {
      identifier: "",
      line: lineOptions.precipitation,
      overriddenTitle: overriddenTitles.precipitation,
    },
  },
  gusture: {
    isVisible: true,
    dataset: {
      identifier: "",
      line: lineOptions.gusture,
      overriddenTitle: overriddenTitles.gusture,
    },
  },
  dateSelection: { isVisible: false },
};

function WeatherMonitoringEditor({
  config,
  onConfigChange,
}: DashboardEditorProps<WeatherMonitoringProps>) {
  const [currentConfig, setCurrentConfig] = useState<WeatherMonitoringProps>({
    ...defaultConfig,
    ...config,
  });

  useEffect(() => {
    setCurrentConfig({ ...defaultConfig, ...config });
  }, [config]);

  const handleSwitchChange = (name: WeatherMonitoringKey, value: boolean) => {
    const currentValue = currentConfig[name];
    const updatedConfig: WeatherMonitoringProps = {
      ...currentConfig,
      [name]: {
        ...currentValue,
        isVisible: value,
      },
    };
    setCurrentConfig(updatedConfig);
    onConfigChange(updatedConfig);
  };

  const handleDatasetChange = (e: string | null, name: WeatherMonitoringKey) => {
    const currentValue = currentConfig[name];
    const updatedConfig: WeatherMonitoringProps = {
      ...currentConfig,
      [name]: {
        ...currentValue,
        isVisible: true,
        dataset: {
          identifier: e ?? "",
          line: lineOptions[name] || { color: "#FF0000", width: 2, dash: "solid" },
          overriddenTitle: overriddenTitles[name] || name,
        },
      },
    };
    setCurrentConfig(updatedConfig);
    onConfigChange(updatedConfig);
  };

  const handleLocationChange = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedConfig: WeatherMonitoringProps = {
      ...currentConfig,
      location: e.target.value,
    };
    setCurrentConfig(updatedConfig);
    onConfigChange(updatedConfig);
  };

  return (
    <div>
      <div className="flex flex-row justify-content-start gap-5">
        <div className="flex flex-column gap-2">
          <label htmlFor="timeInterval">{T("word.time-interval")}</label>
          <Dropdown
            value={currentConfig.timeInterval}
            options={Object.values(TimeOptions).map(option => ({
              label: option.value,
              value: option.name,
            }))}
            onChange={e => {
              const updatedConfig: WeatherMonitoringProps = {
                ...currentConfig,
                timeInterval: e.value,
              };
              setCurrentConfig(updatedConfig);
              onConfigChange(updatedConfig);
            }}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="location">{T("word.location")}</label>
          <InputText
            id="location"
            value={currentConfig.location ?? ""}
            onChange={handleLocationChange}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="showDateSelection">{T("chart-config.show-date-selection")}</label>
          <InputSwitch
            id="showDateSelection"
            checked={currentConfig.dateSelection?.isVisible ?? false}
            onChange={() =>
              handleSwitchChange("dateSelection", !currentConfig.dateSelection?.isVisible)
            }
          />
        </div>
      </div>
      <div className="grid mt-5">
        {[
          { name: "temperature", label: T("label.weather-monitoring.temperature-dataset") },
          { name: "humidity", label: T("label.weather-monitoring.humidity-dataset") },
          { name: "windSpeed", label: T("label.weather-monitoring.wind-speed-dataset") },
          { name: "windDirection", label: T("label.weather-monitoring.wind-speed-dataset") },
          { name: "precipitation", label: T("label.weather-monitoring.precipitation-dataset") },
        ].map(item => {
          const name = item.name as WeatherMonitoringKey;
          const currentValue = currentConfig[name];
          return (
            <div className="col-4 mb-3" key={name}>
              <label htmlFor={name}>{item.label}</label>
              <DatasetDropdown
                value={currentValue.dataset.identifier ?? ""}
                onChange={e => handleDatasetChange(e, name)}
              />
            </div>
          );
        })}
      </div>
      <div className="grid mt-5">
        {[
          { name: "gusture", label: T("label.weather-monitoring.gusture-dataset") },
          { name: "feelsLikeTemperature", label: T("label.weather-monitoring.feels-like-temperature-dataset") },
        ].map(item => {
          const name = item.name as WeatherMonitoringKey;
          const currentValue = currentConfig[name];
          const isVisible = currentValue?.isVisible;
          return (
            <div className="col-4 mb-3" key={name}>
              <label htmlFor={name}>{item.label}</label>
              <div className="flex justify-content-between my-3">
                <InputSwitch
                  id={name}
                  checked={isVisible}
                  onChange={() => handleSwitchChange(name, !isVisible)}
                />
              </div>
              <DatasetDropdown
                value={isVisible ? currentValue.dataset.identifier : null}
                onChange={e => handleDatasetChange(e, name)}
                disabled={!isVisible}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WeatherMonitoringEditor;
